/**
 * Schemas Zod compartilhados entre backend e frontend.
 *
 * O contrato é entregável de validação (plano §4.3): estes schemas são a
 * especificação executável do que cada endpoint aceita, e o mesmo objeto valida
 * no formulário React e no DTO NestJS. Divergir é impossível por construção.
 */
import { z } from 'zod';
import { AccessRuleEffect, AccessRuleScope, ApprovalPolicy, AuditAction, DocumentPermissionFlag, DocumentVersionStatus, SignableRecordType, SignatureAuthMethod, SignatureMeaning, SIGNABLE_ACTS, SystemRole, } from './enums.js';
/**
 * Valores de um enum como tupla, PRESERVANDO o literal.
 *
 * O `as [string, ...string[]]` que estava aqui apagava a união: toda
 * `z.enum(enumValues(X))` inferia `string`, e o tipo do contrato deixava de
 * dizer qual conjunto de valores era aceito. O sintoma aparecia longe da causa
 * — `DocumentListFilter['status']` chegava à tela como `string[]` e não casava
 * com `DocumentVersionStatus[]`.
 */
export const enumValues = (e) => Object.values(e);
export const uuidSchema = z.string().uuid('Identificador inválido.');
/**
 * O mesmo identificador, com a mensagem do CAMPO.
 *
 * "Identificador inválido" é o sistema falando de si mesmo. Quem deixou um
 * seletor vazio precisa ler o que ELE tem a fazer — "Escolha o responsável." —,
 * e não o nome interno daquilo que o servidor esperava receber. O formato
 * continua sendo conferido; o que muda é quem a frase serve.
 */
export const uuidSchemaFor = (frase) => z.string().uuid(frase);
/**
 * Booleano vindo de QUERY STRING.
 *
 * `booleanQuerySchema` aplica `Boolean(valor)` — e `Boolean('false')` é `true`.
 * Toda flag que o cliente enviasse explicitamente como `false` chegava ao
 * serviço como `true`. O caso observado em execução: a Lista Mestra manda
 * `onlyPendingMyApproval=false&onlyPendingMyAcknowledgement=false` em toda
 * requisição, o servidor entendia "só o que está pendente PARA MIM" e a tela
 * exibia "Nenhum documento visível para você" com os contadores mostrando 12.
 *
 * O mesmo erro alcançava `users?active=false` (devolvia os ativos),
 * `notifications?onlyUnread=false` (só as não lidas) e
 * `audit?includeCanonicalContent=false` (devolvia o conteúdo mesmo assim).
 *
 * Aceita booleano de verdade (corpo JSON) e as formas textuais usuais.
 */
/**
 * Lista vinda de QUERY STRING.
 *
 * A chave repetida (`?status=A&status=B`) chega como array; com UM valor só
 * (`?status=A`) chega como string, e o schema de array reprovava com
 * `Expected array, received string` — HTTP 400. Ou seja: filtrar a Lista Mestra
 * por UMA situação, que é o caso normal de um clique em chip, quebrava a tela.
 */
export const arrayQuerySchema = (item) => z
    .union([item, z.array(item)])
    // O tipo de retorno é anotado porque a inferência pela união devolveria
    // `item | item[]` e contaminaria todo consumidor do filtro.
    .transform((value) => (Array.isArray(value) ? value : [value]));
export const booleanQuerySchema = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((value) => (typeof value === 'boolean' ? value : value === 'true' || value === '1'));
/**
 * Segundo fator — OPCIONAL de verdade, inclusive quando o campo existe no
 * formulário e está vazio.
 *
 * `z.string().regex(...).optional()` só ignora `undefined`. Um `<input>` de
 * React Hook Form entrega STRING VAZIA, e a validação reprovava com "Código de
 * 6 dígitos." num campo que nem sequer está renderizado — o formulário de login
 * não submetia, sem pedido de rede, sem mensagem na tela e sem `aria-invalid`
 * em nenhum campo visível. Do lado do usuário: clicar em "Entrar" não faz nada.
 *
 * Vazio é AUSENTE. Aqui, e em todo formulário que amarre campo opcional a
 * `<input>`.
 */
const totpSchema = z
    .union([z.literal(''), z.string().trim().regex(/^\d{6}$/, 'Código de 6 dígitos.')])
    .optional()
    .transform((value) => (value === '' ? undefined : value));
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ─────────────────────────────────────────────────────────────────────────────
// Autenticação — §11.300
// ─────────────────────────────────────────────────────────────────────────────
export const loginSchema = z.object({
    username: z.string().trim().min(1, 'Informe o usuário.').max(150),
    password: z.string().min(1, 'Informe a senha.').max(512),
    /** Slug da organização. Ausente = descoberto pelo username, se único. */
    organizationSlug: z.string().trim().max(120).optional(),
    totp: totpSchema,
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Informe a senha atual.'),
    newPassword: z.string().min(1, 'Informe a nova senha.'),
    confirmPassword: z.string().min(1, 'Confirme a nova senha.'),
})
    .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'A confirmação não confere com a nova senha.',
    path: ['confirmPassword'],
})
    .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'A nova senha precisa ser diferente da atual.',
    path: ['newPassword'],
});
/**
 * Troca de senha EXPIRADA — o caminho de saída de §11.300(b).
 *
 * `POST /auth/password` exige sessão. Quando a senha está vencida (ou é a
 * primeira, com `mustChangePassword`), o login recusa com 403 e NÃO abre
 * sessão — como deve ser: senha vencida não dá acesso ao sistema. Sem uma rota
 * própria, o usuário fica preso e "senha expirada" vira "conta perdida".
 *
 * Esta rota reautentica com a CREDENCIAL ATUAL (usuário + senha vencida +
 * segundo fator, quando exigido), troca a senha e continua sem abrir sessão: o
 * usuário entra de novo, já com a senha nova. A senha vencida autoriza uma
 * coisa só — trocar a si mesma.
 */
export const expiredPasswordChangeSchema = z
    .object({
    username: z.string().trim().min(1, 'Informe o usuário.').max(150),
    organizationSlug: z.string().trim().max(120).optional(),
    totp: totpSchema,
    currentPassword: z.string().min(1, 'Informe a senha atual.').max(512),
    newPassword: z.string().min(1, 'Informe a nova senha.'),
    confirmPassword: z.string().min(1, 'Confirme a nova senha.'),
})
    .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'A confirmação não confere com a nova senha.',
    path: ['confirmPassword'],
})
    .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'A nova senha precisa ser diferente da atual.',
    path: ['newPassword'],
});
/**
 * Política de senha por organização — §11.300(b).
 * Os defaults refletem prática corrente para ambiente regulado.
 */
export const passwordPolicySchema = z.object({
    minLength: z.number().int().min(8).max(128).default(12),
    requireUppercase: z.boolean().default(true),
    requireLowercase: z.boolean().default(true),
    requireDigit: z.boolean().default(true),
    requireSymbol: z.boolean().default(true),
    /** 0 = nunca expira. */
    expiryDays: z.number().int().min(0).max(3650).default(90),
    /** Quantas senhas anteriores não podem ser reusadas. */
    historySize: z.number().int().min(0).max(50).default(5),
    /** §11.300(d) — lockout progressivo. */
    maxFailedAttempts: z.number().int().min(3).max(20).default(5),
    lockoutMinutes: z.number().int().min(1).max(1440).default(15),
    /** Multiplicador do lockout a cada novo ciclo de falhas. */
    lockoutBackoffFactor: z.number().min(1).max(10).default(2),
    mfaRequired: z.boolean().default(false),
});
// ─────────────────────────────────────────────────────────────────────────────
// Assinatura eletrônica — §11.50, §11.70, §11.200
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Passo 3 do fluxo (plano §5.1): o usuário digita usuário + senha e o Identity
 * devolve uma prova de uso único.
 *
 * §11.200(a)(1)(i) exige DOIS componentes distintos de identificação. Por isso
 * `username` é obrigatório mesmo já havendo sessão: reconfirmar quem se é faz
 * parte do ato, e impede assinatura por máquina desbloqueada de terceiro.
 */
export const requestSignatureProofSchema = z.object({
    act: z.enum(Object.keys(SIGNABLE_ACTS)),
    recordType: z.enum(enumValues(SignableRecordType)),
    recordId: uuidSchema,
    username: z.string().trim().min(1, 'Confirme seu usuário.'),
    password: z.string().min(1, 'Confirme sua senha.'),
    totp: totpSchema,
});
export const signatureProofSchema = z.object({
    proofId: uuidSchema,
    token: z.string().min(1),
    expiresAt: z.coerce.date(),
});
/** Hash canônico SHA-256 de um registro, em hexadecimal minúsculo. */
export const recordHashSchema = z
    .string()
    .trim()
    .regex(/^[0-9a-f]{64}$/, 'Hash de registro inválido.');
/** Anexado a todo comando que executa um ato assinável. */
export const signedCommandSchema = z.object({
    proofId: uuidSchema,
    proofToken: z.string().min(1),
    /**
     * Obrigatório quando o ato tem `reasonRequired`. Validado no serviço.
     *
     * `nullish`, e não `optional`: quem consome esta prova é o CORE, que monta
     * `reason: input.reason ?? input.comment ?? null` — ou seja, envia `null`
     * quando não há justificativa. Com `optional()` puro o IDENTITY recusava o
     * consumo com 400 "Dados inválidos na requisição", o CORE repassava o mesmo
     * código e o usuário via "Há campos com preenchimento inválido" num diálogo
     * de assinatura que nem tem campo de texto. Na prática, APROVAR SEM
     * COMENTÁRIO era impossível — e comentário é opcional em aprovação
     * (`SIGNABLE_ACTS.DOCUMENT_APPROVE.reasonRequired === false`).
     */
    reason: z.string().trim().max(2000).nullish(),
    /**
     * §11.70 — vínculo assinatura↔registro.
     *
     * O hash canônico do registro TAL COMO O CLIENTE O VIU quando pediu a prova.
     * O servidor recalcula o hash imediatamente antes de aplicar o efeito e
     * recusa com `SIGNATURE.RECORD_MUTATED` se divergir. Fecha a janela entre
     * "pedir a prova" e "assinar": sem isto, um segundo ator poderia emitir nova
     * versão nesse intervalo e o signatário estaria assinando um registro que
     * nunca leu.
     *
     * Opcional para não quebrar integrações existentes; quando ausente, a
     * verificação de §11.70 se limita ao vínculo prova↔`recordId`.
     */
    expectedRecordHash: recordHashSchema.optional(),
});
/** Manifesto renderizado — §11.50(b). Mesmo shape na tela, no PDF/A e na exportação. */
export const signatureManifestEntrySchema = z.object({
    signatureId: uuidSchema,
    meaningCode: z.enum(enumValues(SignatureMeaning)),
    /** Texto CONGELADO no ato. Nunca recalculado a partir do código. */
    meaningText: z.string(),
    signerName: z.string(),
    signerRole: z.string(),
    signedAt: z.coerce.date(),
    /** Fuso da organização, para exibição. Ex.: 'America/Sao_Paulo'. */
    displayTimeZone: z.string(),
    recordLabel: z.string(),
    recordHash: z.string().length(64),
    authMethod: z.enum(enumValues(SignatureAuthMethod)),
    reason: z.string().nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Documento / GED
// ─────────────────────────────────────────────────────────────────────────────
export const accessRuleSchema = z
    .object({
    scope: z.enum(enumValues(AccessRuleScope)),
    effect: z.enum(enumValues(AccessRuleEffect)).default(AccessRuleEffect.ALLOW),
    userId: uuidSchema.nullable().optional(),
    departmentId: uuidSchema.nullable().optional(),
    flags: z
        .array(z.enum(enumValues(DocumentPermissionFlag)))
        .min(1, 'Selecione ao menos uma permissão.'),
})
    .superRefine((rule, ctx) => {
    if (rule.scope === AccessRuleScope.USER && !rule.userId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['userId'],
            message: 'Regra de escopo Usuário exige um colaborador.',
        });
    }
    if (rule.scope === AccessRuleScope.DEPARTMENT && !rule.departmentId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['departmentId'],
            message: 'Regra de escopo Departamento exige um departamento.',
        });
    }
});
/** Limites preservados do legado — ged.md §3.7. */
export const MAX_DOCUMENT_NAME_LENGTH = 255;
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const ALLOWED_FILE_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'odt', 'ods', 'odp', 'txt', 'csv',
    'jpg', 'jpeg', 'png', 'gif',
    'zip', 'rar',
];
const brDateSchema = z.coerce.date({
    errorMap: () => ({ message: 'Data inválida.' }),
});
const documentBaseSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, 'Informe o nome do documento.')
        .max(MAX_DOCUMENT_NAME_LENGTH, `Máximo de ${MAX_DOCUMENT_NAME_LENGTH} caracteres.`),
    documentTypeId: uuidSchema,
    description: z.string().max(65_535).default(''),
    /** Justificativa da versão. Legado: campo `just` → `erp_document_history.comment`. */
    changeJustification: z
        .string()
        .trim()
        .min(1, 'Informe a justificativa desta versão.')
        .max(4000),
    revisionDate: brDateSchema,
    hasValidity: z.boolean().default(false),
    validityMonths: z.coerce.number().int().min(0).max(600).default(0),
    /**
     * Legado: `controle = 'on'`. O rótulo era "Documento Controlado?" mas o efeito
     * era aprovação automática — ged.md BUG-09. Aqui o nome diz o que faz.
     */
    autoApprove: z.boolean().default(false),
    hasControlledPrintedCopy: z.boolean().default(false),
    approvalPolicy: z.enum(enumValues(ApprovalPolicy)).default(ApprovalPolicy.ANY_ONE),
    quorumSize: z.coerce.number().int().min(1).max(50).nullable().default(null),
    /** ISO 13485 §4.2.4 — quem precisa tomar ciência da versão aprovada. */
    distributionUserIds: z.array(uuidSchema).default([]),
    distributionDepartmentIds: z.array(uuidSchema).default([]),
    requiresAcknowledgement: z.boolean().default(false),
    /**
     * Ao menos uma regra é obrigatória. Um documento sem nenhuma regra de acesso
     * é invisível para todo mundo — no legado a listagem usava `JOIN` (não
     * `LEFT JOIN`), então o documento simplesmente sumia (ged.md §5.4), e o
     * formulário barrava esse caso na validação 23. Sem este `.min(1)` seria
     * possível criar, pela API, um documento controlado que ninguém consegue
     * ler nem aprovar — ISO 13485 §4.2.4(d) exige que a versão pertinente esteja
     * disponível nos pontos de uso.
     */
    accessRules: z
        .array(accessRuleSchema)
        .min(1, 'Informe ao menos uma regra de distribuição para o documento.'),
});
/**
 * Coerência entre validade, meses e quórum.
 *
 * FUNÇÃO de refinamento, aplicada com `.superRefine(validityCoherence)` no
 * schema concreto — e não um invólucro genérico.
 *
 * O invólucro anterior era `<T extends z.ZodTypeAny>(schema: T) =>
 * schema.superRefine(...)`. Como `ZodTypeAny` é `ZodType<any, any, any>`, o
 * `Output` do `superRefine` saía `any` e `z.infer<typeof createDocumentSchema>`
 * — logo `CreateDocumentInput` e `CreateDocumentRequest` — degenerava em `any`.
 * O DTO que carrega metadados, validade, política de aprovação e regras de
 * acesso ficava SEM checagem nenhuma, no serviço e no formulário. Aplicada
 * diretamente sobre o objeto concreto, a forma é preservada.
 */
function validityCoherence(data, ctx) {
    if (data.hasValidity && data.validityMonths < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validityMonths'],
            message: 'Informe a quantidade de meses para validade do documento.',
        });
    }
    if (!data.hasValidity && data.validityMonths !== 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validityMonths'],
            message: 'Documento sem validade precisa ter zero meses.',
        });
    }
    if (data.approvalPolicy === ApprovalPolicy.QUORUM && !data.quorumSize) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quorumSize'],
            message: 'Informe o tamanho do quórum.',
        });
    }
}
export const createDocumentSchema = documentBaseSchema
    .extend({
    /** Legado: campo `rev`. Vazio → 1.0. */
    version: z
        .string()
        .trim()
        .regex(/^\d+\.\d+$/, 'Use o formato <maior>.<menor>, ex.: 1.0')
        .optional(),
})
    .superRefine(validityCoherence);
export const createDocumentVersionSchema = documentBaseSchema
    .extend({
    /** Vazio → o sistema calcula pelo ramo A/B (ver @qualicloud/domain/versioning). */
    version: z
        .string()
        .trim()
        .regex(/^\d+\.\d+$/, 'Use o formato <maior>.<menor>, ex.: 2.0')
        .optional(),
})
    .superRefine(validityCoherence);
/**
 * Alteração exclusiva de distribuição — não gera versão. ged.md §6.2
 *
 * Aqui o array VAZIO é permitido de propósito, ao contrário da criação:
 * reproduz a guarda de segurança do legado (`ged.php:419-426`, ged.md §5.7 item 2),
 * em que um POST sem nenhuma regra PRESERVA a distribuição atual em vez de
 * apagá-la. Essa guarda existia porque o formulário perdia os hidden inputs em
 * certas condições e, sem ela, uma edição inocente zerava toda a ACL do documento.
 */
export const updateDistributionSchema = z.object({
    accessRules: z.array(accessRuleSchema),
    distributionUserIds: z.array(uuidSchema).default([]),
    distributionDepartmentIds: z.array(uuidSchema).default([]),
    requiresAcknowledgement: z.boolean().default(false),
});
export const approveDocumentSchema = signedCommandSchema.extend({
    decision: z.enum(['APPROVED', 'REJECTED']),
    comment: z.string().trim().max(4000).optional(),
});
export const obsoleteDocumentSchema = signedCommandSchema.extend({
    reason: z.string().trim().min(1, 'Informe o motivo da obsolescência.').max(500),
    /** Documento que substitui este, quando houver. */
    replacedByDocumentId: uuidSchema.nullable().optional(),
});
/**
 * Filtros da Lista Mestra.
 *
 * DIVERGÊNCIA D-LIST-01: no legado o default (`nameStatus = ''`) era
 * `IN ('A')` SEM excluir vencidos, misturando aprovados e vencidos na mesma
 * lista, enquanto o filtro explícito 'A' excluía vencidos (ged.md §14.1).
 * Comportamento contraditório. Aqui o default é explícito e configurável.
 */
export const documentListFilterSchema = paginationSchema.extend({
    documentTypeId: uuidSchema.optional(),
    /** Inclui subpastas do tipo informado. O legado casava por NOME, sem hierarquia. */
    includeDescendantTypes: booleanQuerySchema.default(true),
    status: arrayQuerySchema(z.enum(enumValues(DocumentVersionStatus))).optional(),
    search: z.string().trim().max(255).optional(),
    expiringWithinDays: z.coerce.number().int().min(0).max(365).optional(),
    onlyPendingMyApproval: booleanQuerySchema.default(false),
    onlyPendingMyAcknowledgement: booleanQuerySchema.default(false),
    sortBy: z
        .enum(['name', 'documentType', 'version', 'revisionDate', 'validUntil', 'status'])
        .default('documentType'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export const acknowledgeDocumentSchema = signedCommandSchema.extend({
    documentVersionId: uuidSchema,
});
/**
 * Painel — `getDashboardSummary()` (ged.md §14.5).
 *
 * `documentTypeId` reproduz o parâmetro `$tipo_id` do legado
 * (`model/ged.php:1010-1012`, `0` = todos). A janela de "vencendo" tem default
 * 30 dias, literalmente `model/ged.php:996`, e agora é configurável.
 */
export const dashboardSummaryQuerySchema = z.object({
    documentTypeId: uuidSchema.optional(),
    includeDescendantTypes: booleanQuerySchema.default(true),
    expiringWithinDays: z.coerce.number().int().min(1).max(365).default(30),
});
/** Contadores por situação da Lista Mestra — `countDoc()` (ged.md §14.4). */
export const documentCountsQuerySchema = z.object({
    documentTypeId: uuidSchema.optional(),
    includeDescendantTypes: booleanQuerySchema.default(true),
    search: z.string().trim().max(255).optional(),
});
/**
 * Listagem ADMINISTRATIVA — tela `ged/ged/admin` (ged.md §14.3, §15.2).
 *
 * Filtros preservados de `model/ged.php:495-506`: `filter_name` → `search`,
 * `filter_tipo` → `documentTypeId`, `filter_status` → `status`. O
 * `filter_store_id` do legado **não tem contraparte e não pode ter**: o tenant
 * vem do JWT e é imposto por RLS, nunca por parâmetro (ged.md R4).
 *
 * A paginação de 50 é a do legado (`ged.php:32`, hardcoded); aqui é o default.
 * `authorUserId` e o recorte por período são acréscimos: a tela administrativa
 * existe para responder "quem publicou o quê e quando", e o legado só permitia
 * filtrar por nome.
 */
export const documentAdminListFilterSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    authorUserId: uuidSchema.optional(),
    documentTypeId: uuidSchema.optional(),
    includeDescendantTypes: booleanQuerySchema.default(true),
    status: arrayQuerySchema(z.enum(enumValues(DocumentVersionStatus))).optional(),
    search: z.string().trim().max(255).optional(),
    /** Período de CRIAÇÃO do documento, inclusive nas duas pontas. */
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
});
/**
 * Vida útil do token de impressão, em segundos.
 *
 * Curta de propósito: o token identifica UMA cópia impressa e é gravado no
 * evento `PRINT` da trilha. Reaproveitá-lo dias depois faria uma segunda cópia
 * herdar a rastreabilidade da primeira.
 */
export const PRINT_TOKEN_TTL_SECONDS = 300;
/**
 * Emissão do token de impressão — `printDocument()` (ged.md §15.15).
 *
 * O legado verificava a permissão derivada da flag `download`
 * (`ged.php:1266-1276`) e gravava `route='ged/ged/printDocument'` em
 * `erp_audit_action` (B11, `ged.php:1284-1317`). As duas coisas são preservadas;
 * o redirect quebrado (BUG-05) não.
 */
export const createPrintTokenSchema = z.object({
    /** Ausente = versão vigente. */
    versionId: uuidSchema.optional(),
    /** Motivo da impressão, quando a organização exigir registro. */
    reason: z.string().trim().max(500).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Tipo de documento (hierarquia por ID — corrige C14)
// ─────────────────────────────────────────────────────────────────────────────
export const documentTypeSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome do tipo.').max(255),
    description: z.string().max(4000).default(''),
    parentId: uuidSchema.nullable().default(null),
    /** Legado: `freq_aviso`, gravado e nunca lido (BUG-11). Aqui alimenta o job. */
    noticeFrequency: z.enum(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY']).default('NONE'),
    defaultValidityMonths: z.coerce.number().int().min(0).max(600).default(0),
    defaultApprovalPolicy: z.enum(enumValues(ApprovalPolicy)).default(ApprovalPolicy.ANY_ONE),
    defaultQuorumSize: z.coerce.number().int().min(1).max(50).nullable().default(null),
    sortOrder: z.coerce.number().int().min(0).default(0),
    active: z.boolean().default(true),
});
// ─────────────────────────────────────────────────────────────────────────────
// Auditoria
// ─────────────────────────────────────────────────────────────────────────────
export const auditQuerySchema = paginationSchema.extend({
    entityType: z.string().trim().max(100).optional(),
    entityId: uuidSchema.optional(),
    actorUserId: uuidSchema.optional(),
    /*
     * `arrayQuerySchema`, e não `z.array` — este era o único filtro de lista do
     * sistema sem o auxiliar, e por isso o único que quebrava.
     *
     * `URLSearchParams` repete a chave para uma lista: com DOIS itens chega
     * `?action=A&action=B` e o Nest entrega um array; com UM item chega
     * `?action=A` e ele entrega uma STRING. O filtro da trilha só permite escolher
     * uma ação por vez, então a tela devolvia 400 em toda seleção — e nunca em
     * nenhuma outra listagem, o que fazia o defeito parecer da tela de auditoria.
     */
    action: arrayQuerySchema(z.enum(enumValues(AuditAction))).optional(),
    occurredFrom: z.coerce.date().optional(),
    occurredTo: z.coerce.date().optional(),
    onlySigned: booleanQuerySchema.default(false),
});
// ─────────────────────────────────────────────────────────────────────────────
// Organização e usuários
// ─────────────────────────────────────────────────────────────────────────────
export const organizationSettingsSchema = z.object({
    name: z.string().trim().min(1).max(255),
    slug: z.string().trim().regex(/^[a-z0-9-]{2,60}$/, 'Use letras minúsculas, números e hífen.'),
    timeZone: z.string().trim().min(1).default('America/Sao_Paulo'),
    locale: z.string().trim().default('pt-BR'),
    passwordPolicy: passwordPolicySchema,
    /** Segregação de funções na aprovação. Default: autor não aprova. */
    allowSelfApproval: z.boolean().default(false),
    /** §11.200(a)(1)(i) — janela de sessão de assinatura. 0 = desligada (default). */
    signatureSessionWindowMinutes: z.number().int().min(0).max(480).default(0),
    /** ISO 13485 §4.2.5 / RDC 665 — mínimo 2 anos; default conservador de 10 anos. */
    recordRetentionYears: z.number().int().min(2).max(50).default(10),
    documentExpiryWarningDays: z.number().int().min(1).max(365).default(30),
    /** §11.100(c) — carta ao FDA. Registrada no onboarding (plano §5.5). */
    part11CertificationLetterSentAt: z.coerce.date().nullable().default(null),
    smtp: z
        .object({
        host: z.string().trim().min(1),
        port: z.number().int().min(1).max(65535),
        secure: z.boolean().default(true),
        username: z.string().trim(),
        fromName: z.string().trim(),
        fromAddress: z.string().email(),
    })
        .nullable()
        .default(null),
});
export const createUserSchema = z.object({
    username: z
        .string()
        .trim()
        .min(3, 'Mínimo de 3 caracteres.')
        .max(150)
        .regex(/^[a-zA-Z0-9._-]+$/, 'Use letras, números, ponto, hífen ou sublinhado.'),
    email: z.string().trim().email('E-mail inválido.'),
    firstName: z.string().trim().min(1, 'Informe o nome.').max(120),
    lastName: z.string().trim().min(1, 'Informe o sobrenome.').max(120),
    /** Congelado em cada assinatura como `signer_role` — §11.50(a). */
    jobTitle: z.string().trim().min(1, 'Informe o cargo.').max(150),
    role: z.enum(enumValues(SystemRole)).default(SystemRole.MEMBER),
    departmentIds: z.array(uuidSchema).default([]),
    active: z.boolean().default(true),
});
export const departmentSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome do departamento.').max(255),
    description: z.string().max(2000).default(''),
    parentId: uuidSchema.nullable().default(null),
    managerUserIds: z.array(uuidSchema).default([]),
});
//# sourceMappingURL=schemas.js.map